import React from 'react';
import { withRouter } from '../../../services/withRoute';
import Service from '../../../services/graphQlServices';
import { SHOOTER_AVAILABILITY_QUERY } from '../../../controllers/crewAvailabilityGql';
import constants from '../../../utils/constants';
import _ from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CrewPlannerRow from './CrewPlannerRow';
import DemandRow from './DemandRow';
import ShooterSidebar from './ShooterSidebar';
import LoadingBar from '../../components/global/loadingBar';
import Filter from '../../components/filter';
import { ReactComponent as SearchIcon } from '../../../assets/svg/search.svg';
import '../../../assets/scss/crewPlanner.scss';

class CrewPlanner extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			error: null,
			viewMode: 'week',
			startDate: parseInt(moment().startOf('week').format('YYYYMMDD')),
			shooters: [],
			demand: [],
			roleActiveFilter: [],
			workspaces: [],
			workspacesFilters: [],
			workspaceActiveFilter: [],
			workspaceFilterCleared: false,
			cityFilters: [],
			cityActiveFilter: [],
			customDateRange: null,
			currentPage: 1,
			pageSize: 25,
			selectedShooter: null,
			selectedDate: null,
			sidebarOpen: false,
			searchQuery: '',
		};
		this.timelineCache = {};
		this._fetchId = 0;
	}

	componentDidMount() {
		const vendorConfig = constants.userAccess.vendors?.[this.props.params.vendorID];
		if (vendorConfig?.activeWorkspaces?.length > 0) {
			this.setState({ workspaceActiveFilter: vendorConfig.activeWorkspaces.map(w => w._id) });
		}
		this.fetchData();
	}

	getDateRange() {
		const { viewMode, startDate, customDateRange } = this.state;
		if (viewMode === 'custom' && customDateRange) {
			return {
				startDate: parseInt(moment(customDateRange[0]).format('YYYYMMDD')),
				endDate: parseInt(moment(customDateRange[1] || customDateRange[0]).format('YYYYMMDD')),
			};
		}
		const start = moment(String(startDate), 'YYYYMMDD');
		let days;
		if (viewMode === 'week') days = 7;
		else if (viewMode === '2weeks') days = 14;
		else days = start.daysInMonth();
		const endDate = parseInt(
			moment(start)
				.add(days - 1, 'days')
				.format('YYYYMMDD')
		);
		return { startDate, endDate };
	}

	getDatesArray() {
		const { viewMode, startDate, customDateRange } = this.state;
		if (viewMode === 'custom' && customDateRange) {
			const start = moment(customDateRange[0]);
			const end = moment(customDateRange[1] || customDateRange[0]);
			const days = end.diff(start, 'days') + 1;
			const dates = [];
			for (let i = 0; i < days; i++) {
				dates.push(parseInt(moment(start).add(i, 'days').format('YYYYMMDD')));
			}
			return dates;
		}
		const start = moment(String(startDate), 'YYYYMMDD');
		const days =
			viewMode === 'week' ? 7 : viewMode === '2weeks' ? 14 : start.daysInMonth();
		const dates = [];
		for (let i = 0; i < days; i++) {
			dates.push(parseInt(moment(start).add(i, 'days').format('YYYYMMDD')));
		}
		return dates;
	}

	getActiveTenantIds = () => {
		if (_.size(this.state.workspaceActiveFilter) > 0) {
			return this.state.workspaceActiveFilter;
		}
		if (this.state.workspaceFilterCleared) return null;
		const vendorConfig = constants.userAccess.vendors?.[this.props.params.vendorID];
		if (vendorConfig?.activeWorkspaces?.length > 0) {
			return vendorConfig.activeWorkspaces.map(w => w._id);
		}
		return null;
	};

	async fetchData() {
		const fetchId = ++this._fetchId;
		this.setState({ loading: true, error: null });
		const { startDate, endDate } = this.getDateRange();
		const vendorId = this.props.params.vendorID;
		const variables = { startDate, endDate, vendorId };
		const tenantIds = this.getActiveTenantIds();
		if (tenantIds) {
			variables.tenantIds = tenantIds;
		}
		if (_.size(this.state.cityActiveFilter) > 0) {
			variables.cities = this.state.cityActiveFilter;
		}
		if (_.size(this.state.roleActiveFilter) > 0) {
			variables.roles = this.state.roleActiveFilter;
		}
		const [success, response] = await Service.query(
			SHOOTER_AVAILABILITY_QUERY,
			variables,
			vendorId,
			localStorage.getItem('usertoken'),
			'vendors'
		);
		if (fetchId !== this._fetchId) return;
		if (success) {
			const data = response.data.shooterAvailability;
			const vendorInfo = response.data.vendorInfo;
			const cityFilters = this.extractCityFilters(data.shooters || []);
			const stateUpdate = {
				shooters: data.shooters || [],
				demand: data.demand || [],
				cityFilters,
				loading: false,
				currentPage: 1,
			};
			if (vendorInfo?.associatedTenants && this.state.workspacesFilters.length === 0) {
				stateUpdate.workspaces = vendorInfo.associatedTenants;
				stateUpdate.workspacesFilters = vendorInfo.associatedTenants.map(t => ({
					key: t._id,
					name: t.workspaceId,
				}));
			}
			this.setState(stateUpdate);
		} else {
			this.setState({ error: 'Failed to load crew data', loading: false });
		}
	}

	extractCityFilters(shooters) {
		const citySet = new Set();
		shooters.forEach((s) => {
			s.dates?.forEach((d) => {
				d.events?.forEach((e) => {
					if (e.city) citySet.add(e.city);
				});
			});
		});
		return Array.from(citySet).sort().map(c => ({ key: c, name: c }));
	}

	navigatePeriod = (direction) => {
		const { viewMode, startDate, customDateRange } = this.state;
		if (viewMode === 'custom' && customDateRange) {
			const rangeStart = moment(customDateRange[0]);
			const rangeEnd = moment(customDateRange[1] || customDateRange[0]);
			const rangeDays = rangeEnd.diff(rangeStart, 'days') + 1;
			const newStart = moment(rangeStart).add(direction * rangeDays, 'days');
			const newEnd = moment(newStart).add(rangeDays - 1, 'days');
			this.setState(
				{ customDateRange: [newStart.toDate(), newEnd.toDate()] },
				() => this.fetchData()
			);
			return;
		}
		const start = moment(String(startDate), 'YYYYMMDD');
		let newStart;
		if (viewMode === 'month') {
			newStart = start.add(direction, 'month').startOf('month');
		} else {
			const days = viewMode === '2weeks' ? 14 : 7;
			newStart = start.add(direction * days, 'days');
		}
		this.setState(
			{ startDate: parseInt(newStart.format('YYYYMMDD')) },
			() => this.fetchData()
		);
	};

	handlePrevPeriod = () => this.navigatePeriod(-1);
	handleNextPeriod = () => this.navigatePeriod(1);

	handleViewChange = (mode) => {
		const { startDate } = this.state;
		let newStart;
		if (mode === 'month') {
			newStart = moment(String(startDate), 'YYYYMMDD').startOf('month');
		} else {
			newStart = moment(String(startDate), 'YYYYMMDD').startOf('week');
		}
		this.setState(
			{ viewMode: mode, startDate: parseInt(newStart.format('YYYYMMDD')), customDateRange: null },
			() => this.fetchData()
		);
	};

	handleGoToToday = () => {
		const today = moment();
		const newStart = this.state.viewMode === 'month'
			? today.startOf('month')
			: today.startOf('week');
		this.setState(
			{ startDate: parseInt(newStart.format('YYYYMMDD')), viewMode: this.state.viewMode === 'custom' ? 'week' : this.state.viewMode, customDateRange: null },
			() => this.fetchData()
		);
	};

	handleDateJump = (date) => {
		if (!date) return;
		const m = moment(date);
		const newStart = this.state.viewMode === 'month'
			? m.startOf('month')
			: m.startOf('week');
		this.setState(
			{ startDate: parseInt(newStart.format('YYYYMMDD')), viewMode: this.state.viewMode === 'custom' ? 'week' : this.state.viewMode, customDateRange: null },
			() => this.fetchData()
		);
	};

	handleCellClick = (shooter, date) => {
		this.setState({
			selectedShooter: shooter,
			selectedDate: date,
			sidebarOpen: true,
		});
	};

	handleSearch = (e) => {
		this.setState({ searchQuery: e.target.value, currentPage: 1 });
	};

	applyWorkspaceFilter = (filter) => {
		this.setState(
			{ workspaceActiveFilter: filter, workspaceFilterCleared: false, currentPage: 1 },
			() => this.fetchData()
		);
	};

	clearWorkspaceFilter = () => {
		this.setState(
			{ workspaceActiveFilter: [], workspaceFilterCleared: true, currentPage: 1 },
			() => this.fetchData()
		);
	};

	applyCityFilter = (filter) => {
		this.setState(
			{ cityActiveFilter: filter, currentPage: 1 },
			() => this.fetchData()
		);
	};

	clearCityFilter = () => {
		this.setState(
			{ cityActiveFilter: [], currentPage: 1 },
			() => this.fetchData()
		);
	};

	applyRoleFilter = (filter) => {
		this.setState(
			{ roleActiveFilter: filter, currentPage: 1 },
			() => this.fetchData()
		);
	};

	clearRoleFilter = () => {
		this.setState(
			{ roleActiveFilter: [], currentPage: 1 },
			() => this.fetchData()
		);
	};

	handleDateRangeChange = (dates) => {
		if (!dates) {
			this.setState(
				{ customDateRange: null, viewMode: 'week', startDate: parseInt(moment().startOf('week').format('YYYYMMDD')) },
				() => this.fetchData()
			);
			return;
		}
		const [start, end] = Array.isArray(dates) ? dates : [dates, dates];
		if (start && end) {
			this.setState(
				{ customDateRange: [start, end], viewMode: 'custom' },
				() => this.fetchData()
			);
		} else if (start) {
			this.setState({ customDateRange: [start, null] });
		}
	};

	getFilteredShooters() {
		const { shooters, searchQuery } = this.state;
		let filtered = shooters;

		if (searchQuery.trim()) {
			const q = searchQuery.trim().toLowerCase();
			filtered = filtered.filter((s) => s.name?.toLowerCase().includes(q));
		}

		return filtered;
	}

	getDateRangeLabel() {
		const { startDate, viewMode, customDateRange } = this.state;
		if (viewMode === 'custom' && customDateRange) {
			const start = moment(customDateRange[0]);
			const end = moment(customDateRange[1] || customDateRange[0]);
			if (start.isSame(end, 'day')) return start.format('MMM D, YYYY');
			return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
		}
		const start = moment(String(startDate), 'YYYYMMDD');
		if (viewMode === 'month') {
			return start.format('MMMM YYYY');
		}
		const days = viewMode === 'week' ? 6 : 13;
		const end = moment(start).add(days, 'days');
		return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
	}

	getSummaryCounts(filteredShooters, dates) {
		let freeCount = 0;
		let partialCount = 0;
		let busyCount = 0;

		filteredShooters.forEach((s) => {
			const hasBooked = dates.some((d) => {
				const dateInfo = s.dates?.find((sd) => sd.date === d);
				return dateInfo && dateInfo.status !== 'AVAILABLE';
			});
			const allBooked = dates.every((d) => {
				const dateInfo = s.dates?.find((sd) => sd.date === d);
				return dateInfo && dateInfo.status === 'BUSY';
			});

			if (allBooked) busyCount++;
			else if (hasBooked) partialCount++;
			else freeCount++;
		});

		return { freeCount, partialCount, busyCount };
	}

	formatDateHeader(date) {
		const m = moment(String(date), 'YYYYMMDD');
		return (
			<>
				<div className="header-day">{m.format('ddd')}</div>
				<div className="header-date">{m.format('D')}</div>
			</>
		);
	}

	render() {
		const {
			loading,
			error,
			viewMode,
			demand,
			currentPage,
			pageSize,
			sidebarOpen,
			selectedShooter,
			selectedDate,
			searchQuery,
			customDateRange,
			shooters,
		} = this.state;

		const dates = this.getDatesArray();
		const filteredShooters = this.getFilteredShooters();
		const startIdx = (currentPage - 1) * pageSize;
		const paginatedShooters = filteredShooters.slice(startIdx, startIdx + pageSize);
		const totalPages = Math.ceil(filteredShooters.length / pageSize);
		const dateRangeLabel = this.getDateRangeLabel();
		const { freeCount, partialCount, busyCount } = this.getSummaryCounts(
			filteredShooters,
			dates
		);

		const roleFilters = [
			{ key: 'photographer_candid', name: 'Candid - Photographer' },
			{ key: 'photographer_traditional', name: 'Traditional - Photographer' },
			{ key: 'cinematographer_candid', name: 'Candid - Cinematographer' },
			{ key: 'cinematographer_traditional', name: 'Traditional - Cinematographer' },
		];

		const isFirstLoad = loading && shooters.length === 0;
		if (isFirstLoad) {
			return (
				<div className="layout-wrapper">
					<div className="container">
						<div className="crew-planner-page">
							<div className="crew-planner-loading">
								<LoadingBar />
							</div>
						</div>
					</div>
				</div>
			);
		}

		if (error) {
			return (
				<div className="layout-wrapper">
					<div className="container">
						<div className="crew-planner-page">
							<div className="crew-planner-error">{error}</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="layout-wrapper">
				<div className="container">
					<div className="crew-planner-page">
						<div className="crew-planner-header">
							<a className="title">Crew Planner</a>
							<div className="crew-planner-search">
								<span className="search-icon">
									<SearchIcon />
								</span>
								<input
									placeholder="Search shooter"
									value={searchQuery}
									onChange={this.handleSearch}
								/>
							</div>
						</div>

						<div className="crew-planner-filters">
							<div className="crew-planner-nav">
								<button onClick={this.handlePrevPeriod}>&larr;</button>
								<span className="date-label" onClick={() => this.dateJumpRef?.setOpen(true)}>{dateRangeLabel}</span>
								<DatePicker
									ref={(ref) => { this.dateJumpRef = ref; }}
									selected={moment(String(this.state.startDate), 'YYYYMMDD').toDate()}
									onChange={this.handleDateJump}
									customInput={<span style={{ display: 'none' }} />}
									dateFormat="MMM d, yyyy"
								/>
								<button onClick={this.handleNextPeriod}>&rarr;</button>
							</div>
							<div className="crew-planner-view-toggle">
								<button onClick={this.handleGoToToday}>Today</button>
								{['week', '2weeks', 'month'].map((mode) => (
									<button
										key={mode}
										className={viewMode === mode ? 'active' : ''}
										onClick={() => this.handleViewChange(mode)}
									>
										{mode === '2weeks'
											? '2 Weeks'
											: mode.charAt(0).toUpperCase() + mode.slice(1)}
									</button>
								))}
								<button
									className={viewMode === 'custom' ? 'active' : ''}
									onClick={() => {
										if (viewMode !== 'custom') {
											this.setState({ viewMode: 'custom', customDateRange: null });
										}
									}}
								>
									Custom
								</button>
							</div>
							{viewMode === 'custom' && (
								<div className="crew-planner-datepicker">
									<DatePicker
										selected={customDateRange?.[0] || null}
										onChange={this.handleDateRangeChange}
										startDate={customDateRange?.[0]}
										endDate={customDateRange?.[1]}
										selectsRange
										placeholderText="Select date range"
										dateFormat="MMM d, yyyy"
										isClearable
									/>
								</div>
							)}
							<div className="shoots-header-filters-container">
								<Filter
									type={'workspace'}
									label={'Workspaces'}
									version="v2"
									filters={this.state.workspacesFilters}
									activeFilter={this.state.workspaceActiveFilter}
									clearSelection={this.clearWorkspaceFilter}
									isMultipleSelectable={true}
									filterType="shoot"
									applyFilter={this.applyWorkspaceFilter}
								/>
								<Filter
									type={'city'}
									label={'City'}
									version="v2"
									filters={this.state.cityFilters}
									activeFilter={this.state.cityActiveFilter}
									clearSelection={this.clearCityFilter}
									isMultipleSelectable={true}
									filterType="shoot"
									applyFilter={this.applyCityFilter}
								/>
								<Filter
									type={'role'}
									label={'Role'}
									version="v2"
									filters={roleFilters}
									activeFilter={this.state.roleActiveFilter}
									clearSelection={this.clearRoleFilter}
									isMultipleSelectable={true}
									filterType="shoot"
									applyFilter={this.applyRoleFilter}
								/>
							</div>
							<div className="crew-planner-summary-inline">
								<span className="summary-item">
									<span className="summary-dot green" /> {freeCount} Available
								</span>
								<span className="summary-item">
									<span className="summary-dot yellow" /> {partialCount} Partial
								</span>
								<span className="summary-item">
									<span className="summary-dot red" /> {busyCount} Busy
								</span>
							</div>
						</div>

						<div className={`crew-planner-grid ${loading ? 'refetching' : ''}`}>
							<div className="crew-planner-header-row">
								<div className="crew-planner-name-col">Shooter</div>
								{dates.map((d) => (
									<div key={d} className="crew-planner-date-col">
										{this.formatDateHeader(d)}
									</div>
								))}
								<div className="crew-planner-total-col">Total</div>
							</div>

							<DemandRow demand={demand} dates={dates} vendorId={this.props.params.vendorID} />

							{paginatedShooters.map((shooter) => (
								<CrewPlannerRow
									key={shooter.userId}
									shooter={shooter}
									dates={dates}
									onCellClick={this.handleCellClick}
								/>
							))}
						</div>

						{totalPages > 1 && (
							<div className="crew-planner-pagination">
								<span className="page-info">
									{startIdx + 1} -{' '}
									{Math.min(startIdx + pageSize, filteredShooters.length)} of{' '}
									{filteredShooters.length}
								</span>
								<span className="page-controls">
									<a
										className={currentPage === 1 ? 'disabled' : ''}
										onClick={() =>
											currentPage > 1 &&
											this.setState({ currentPage: currentPage - 1 })
										}
									>
										&lsaquo;
									</a>
									{Array.from({ length: totalPages }, (_, i) => i + 1).map(
										(page) => (
											<a
												key={page}
												className={currentPage === page ? 'active' : ''}
												onClick={() =>
													this.setState({ currentPage: page })
												}
											>
												{page}
											</a>
										)
									)}
									<a
										className={currentPage === totalPages ? 'disabled' : ''}
										onClick={() =>
											currentPage < totalPages &&
											this.setState({ currentPage: currentPage + 1 })
										}
									>
										&rsaquo;
									</a>
								</span>
							</div>
						)}
					</div>

					{sidebarOpen && (
						<ShooterSidebar
							shooter={selectedShooter}
							anchorDate={selectedDate}
							onClose={() => this.setState({ sidebarOpen: false })}
							vendorId={this.props.params.vendorID}
							cache={this.timelineCache}
						/>
					)}
				</div>
			</div>
		);
	}
}

export default withRouter(CrewPlanner);
